import React from 'react';
import { Thermometer, Drop } from '@phosphor-icons/react';
import { useI18n } from '../contexts/I18nContext';
import { useRelativeTime } from '../lib/time';

const fmt = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toFixed(1) : null);

/**
 * Live temperature/humidity indicator for a room or an H4 Pro device.
 * Renders NOTHING when neither value is present (not every room has a sensor).
 * `compact` → inline chips for list rows; default → a bordered tile.
 */
export function SensorReadings({ temperature, humidity, at, compact = false, testid = 'sensor-readings' }) {
  const { t } = useI18n();
  const rel = useRelativeTime();
  const tv = fmt(temperature);
  const hv = fmt(humidity);
  if (tv == null && hv == null) return null;

  if (compact) {
    return (
      <span className="inline-flex items-center gap-3 text-sm text-ink-soft" data-testid={testid}>
        {tv != null && (
          <span className="inline-flex items-center gap-1.5" title={t('sensor_temperature')}>
            <Thermometer size={14} weight="duotone" className="text-terracotta-700" />
            {tv}°C
          </span>
        )}
        {hv != null && (
          <span className="inline-flex items-center gap-1.5" title={t('sensor_humidity')}>
            <Drop size={14} weight="duotone" className="text-sky-600" />
            {hv}%
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="rounded-xl border border-stone_line p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-3">
        {t('sensor_climate')}
      </div>
      <div className="flex items-center gap-6 flex-wrap">
        {tv != null && (
          <div className="flex items-center gap-2.5" data-testid={`${testid}-temperature`}>
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-terracotta-100 text-terracotta-700 shrink-0">
              <Thermometer size={18} weight="duotone" />
            </div>
            <div>
              <div className="text-lg font-medium text-ink leading-none">
                {tv}<span className="text-sm text-ink-soft ml-0.5">°C</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-muted mt-1">{t('sensor_temperature')}</div>
            </div>
          </div>
        )}
        {hv != null && (
          <div className="flex items-center gap-2.5" data-testid={`${testid}-humidity`}>
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-sky-100 text-sky-700 shrink-0">
              <Drop size={18} weight="duotone" />
            </div>
            <div>
              <div className="text-lg font-medium text-ink leading-none">
                {hv}<span className="text-sm text-ink-soft ml-0.5">%</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-ink-muted mt-1">{t('sensor_humidity')}</div>
            </div>
          </div>
        )}
      </div>
      {at && (
        <div className="text-[11px] text-ink-muted mt-3" data-testid={`${testid}-time`}>
          {t('sensor_last_reading')}: {rel(at)}
        </div>
      )}
    </div>
  );
}
